import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningOutlinedIcon from '@mui/icons-material/WarningOutlined';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Button,
  Container,
  Grid,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const CompanyBeneficial = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="FinCEN BOIR - Company Applicant VS Beneficial Owners"
          description="Learn the difference between company applicant and beneficial owner in the Beneficial Ownership report and file with confidence"
          keywords=""
          canonical="resources/fincen-boir/company-applicant-vs-beneficial-owner/"
          ogType="article"
          jsonLdPricing="39.99"
          jsonLdAggregateRatingValue="4.97"
          jsonLdAggregateRatingCount="3830"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Alert
          severity="warning"
          icon={<WarningOutlinedIcon sx={{ fontSize: 44 }} />}
          sx={{ borderRadius: 3, mt: 3 }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1, sm: 2 }}
            justifyContent="space-around"
            alignItems="center"
          >
            <Typography
              component="div"
              variant="body1"
              sx={{
                textAlign: { xs: 'left', sm: 'left' },
                fontSize: { xs: 13, sm: 14, md: 15 },
                //  color: '#ccc',
                lineHeight: 1.3,
              }}
            >
              <AlertTitle sx={{ fontWeight: 700, mb: 0 }}>
                BOI Compliance Update:
              </AlertTitle>
              No reporting is required for U.S. businesses and citizens. Only
              Foreign Reporting Companies must comply.{' '}
              <Link
                href="https://blog.taxzerone.com/posts/boi-report-fincen-revises-reporting-rules-now-focused-on-foreign-entities/"
                target="_blank"
                sx={{ color: '#0202ff' }}
              >
                See Details
              </Link>
              .
            </Typography>
          </Stack>
        </Alert>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mt: 3,
          }}
        >
          BOIR:{' '}
          <span className="highlight-container">
            <span className="highlight">
              {' '}
              Company Applicant VS Beneficial Owners
            </span>
          </span>{' '}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Essential information about the BOI Report submission
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=boircompany`}
          >
            E-File Now!
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs
            items={[
              { label: 'Essential Forms', path: '/resources/essential-forms/' },
            ]}
          />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Typography variant="body1" sx={{ mb: 4 }}>
                  When submitting the{' '}
                  <Link
                    href="/resources/fincen-boir/boi-reporting-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    BOI Report
                  </Link>{' '}
                  to FinCEN, the filer needs to understand the difference
                  between the Company Applicant and the Beneficial Owner.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is a BOI Report?
                </Typography>

                <Typography variant="body1" sx={{ mb: 4 }}>
                  A BOI (Beneficial Ownership Information) Report details the
                  individuals who ultimately own or control the company. For
                  more information, please read the{' '}
                  <Link
                    href="/resources/fincen-boir/boi-reporting-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    General Instructions for BOI Reporting
                  </Link>
                  .
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who is the Company Applicant?
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  The Company Applicant is:
                </Typography>
                <ol style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    <b>The individual who directly filed the document</b> that
                    created a domestic reporting company, or the individual who
                    directly filed the document that first registered a foreign
                    reporting company. This person would have physically or
                    electronically filed the document with the secretary of
                    state or a similar office.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <b>
                      The individual who was primarily responsible for directing
                      or controlling the filing
                    </b>{' '}
                    of the creation or first registration document. Even if this
                    person did not file the document with the secretary of state
                    or a similar office, they are still considered a company
                    applicant.
                  </li>
                </ol>

                <Alert
                  icon={<InfoOutlinedIcon />}
                  severity="info"
                  sx={{ mt: 0, mb: 4 }}
                >
                  <b>Important Notes:</b>
                  <Box
                    component="ul"
                    sx={{
                      mb: '0 !important',
                      '& li': { mb: 1 },
                      '& li:last-child': { mb: 0 },
                    }}
                  >
                    <li>
                      Company applicant details are required only if the company
                      is created or registered on or{' '}
                      <span style={{ fontWeight: 600 }}>
                        after January 1, 2024
                      </span>
                      .
                    </li>
                    <li>
                      A maximum of two company applicants are allowed to be
                      filed.
                    </li>
                    <li>
                      Required details include the name, date of birth, address,
                      and identification document details of the company
                      applicant.
                    </li>
                    <li>
                      Once the Company applicant is added, the company applicant
                      cannot be removed from the BOI Report
                    </li>
                  </Box>
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who is a Beneficial Owner?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  A Beneficial Owner is an individual who has the:
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    <b>Ownership Interest:</b> A Beneficial Owner is any
                    individual owning or controlling <b>25% or more</b> of a
                    reporting company&apos;s ownership interest. This includes
                    equity, stock, voting rights, capital gains, profit
                    interests, or rights to purchase or sell such interests.
                    Contracts or relationships establishing ownership also
                    qualify.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <b>Substantial Control:</b> An individual with direct or
                    indirect influence over a company&apos;s decision-making is
                    also a Beneficial Owner. This includes senior officers
                    (e.g., CEO, CFO), those with authority to appoint/remove
                    officers, or anyone exercising significant control through
                    influence or indirect methods.
                  </li>
                </ul>

                <Alert
                  icon={<InfoOutlinedIcon />}
                  severity="info"
                  sx={{ mt: 2, mb: 2 }}
                >
                  <b>Important Notes:</b>
                  <Box
                    component="ul"
                    sx={{
                      mb: '0 !important',
                      '& li': { mb: 1 },
                      '& li:last-child': { mb: 0 },
                    }}
                  >
                    <li>
                      There is no limit to the number of beneficial owners.
                    </li>
                    <li>
                      Required details include the name, date of birth, address,
                      and identification document details of the beneficial
                      owner.
                    </li>
                    <li>
                      Beneficial Owners can be removed or added after filing an
                      initial report in situations like the sale of shares,
                      inheritance, or sale of the company itself and so on.
                    </li>
                  </Box>
                </Alert>
                <Box
                  component="div"
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 0, lineHeight: 1.3 }}
                      >
                        Submit Your BOI Report Now with TaxZerone
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 130, textAlign: 'center' }}
                      id="common"
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=boircompany`}
                      >
                        E-file Now!
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Company Applicant vs Beneficial Owner
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              width: 100,
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Criteria
                          </TableCell>
                          <TableCell
                            sx={{
                              width: 300,
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Company Applicant
                          </TableCell>
                          <TableCell sx={{ width: 300, fontWeight: 600 }}>
                            Beneficial Owners
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Definition
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Individuals who filed or directed the filing of the
                            company&apos;s formation or registration documents.
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            An individual who owns or controls 25% of the
                            company&apos;s ownership interest or has substantial
                            control over the company.
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Required
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Only for companies created or registered on or after
                            January 1, 2024.
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Always required, regardless of the company&apos;s
                            creation date.
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Number Allowed
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            A maximum of two company applicants can be filed.
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            No limit to the number of beneficial owners.
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Possible Overlap
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Company applicants Can also be a beneficial owner.
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Beneficial Owner Can also be a company applicant.
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Change
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Company Applicant cannot be removed from BOI Initial
                            report once filed
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Beneficial Owner can be changed after submitting the
                            initial report
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information given in the article is taken from the
                        official website of FinCEN - A Federal Government
                        Agency.{' '}
                        <ul>
                          <li>
                            <Link
                              href="https://www.fincen.gov/boi"
                              sx={{ color: '#0202ff' }}
                              target="_blank"
                            >
                              FinCEN
                            </Link>
                          </li>
                          <li>
                            <Link
                              href="https://www.fincen.gov/boi-faqs"
                              sx={{ color: '#0202ff' }}
                              target="_blank"
                            >
                              FinCEN FAQs
                            </Link>
                          </li>
                        </ul>
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 2,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Submitting a BOI Report is now easier than ever!
            </Typography>
            <Typography
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              File your BOI Report with TaxZerone for just <b>$39.99</b> per
              report!
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=boircompany`}
              >
                E-file Now!
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default CompanyBeneficial;
